import _ from 'lodash';

/*
    This parser is modelled after and functionally identical to
    Statoil.TI.CommonLibrary.Repository.Sql.SqlParameterCrawler.

    If you change this implementation, check if you need to
    change the C# version as well.
*/
export function getParameters(source: string): string[] {
    const result: string[] = [];
    let position = 0;

    const EOF = () => position >= source.length;

    function isAllowedParameterNameStartCharacter(charCode?: number): boolean {
        charCode ||= source.charCodeAt(position);

        if (charCode >= 97 && charCode <= 122)
            return true; // Alpha, lowercase (a..z)

        if (charCode >= 65 && charCode <= 90)
            return true; // Alpha, uppercase (A..Z)

        if (charCode === 95)
            return true; // underscore (_)

        return false;
    }

    function isAllowedParameterNameCharacter(): boolean {
        const charCode = source.charCodeAt(position);

        return isAllowedParameterNameStartCharacter(charCode)
            || (charCode >= 48 && charCode <= 57); // digit (0..9)
    }

    function parseParameter(): string {
        const atSignPosition = position;

        while (true) {
            position++;

            if (EOF()) {
                if (position > atSignPosition + 1) {
                    return source.substring(atSignPosition + 1, position);
                } else {
                    throw Error('@ can\'t be the last character of a SQL query');
                }
            }

            if (position > atSignPosition + 1 && !isAllowedParameterNameCharacter()) {
                return source.substring(atSignPosition + 1, position);
            }
            if (position === atSignPosition + 1 && !isAllowedParameterNameStartCharacter()) {
                throw Error(`Illegal first character in parameter name (${source.charAt(position)})`);
            }
        }
    }

    function consumeLiteral(endChar: string): void {
        const startPosition = position;
        while (true) {
            position++;

            if (EOF()) {
                throw Error(`Missing end quote ${endChar} in SQL query`);
            }

            if (source.charAt(position) === endChar) {
                if (source.charAt(position - 1) === endChar && position !== startPosition + 1) {
                    // End char at position is quoted
                    continue;
                } else if (position + 1 < source.length && source.charAt(position + 1) === endChar) {
                    // Position is quoting the next character
                    continue;
                } else {
                    // End quote reached
                    position++;
                    return;
                }
            }
        }
    }

    while (!EOF()) {
        const c = source.charAt(position);
        switch (c) {
            case '@':
                result.push(parseParameter());
                break;
            case '\'':
            case '"':
                consumeLiteral(c);
                break;
            case '[':
                consumeLiteral(']');
                break;
            default:
                position++;
                break;
        }
    }

    return _.uniqBy(result, x => x.toLowerCase());
}
